import { useCallback, useState } from 'react'

export const TEMPLATE_SCOPES = ['mine', 'school']
export const VISIBILITY_PRIVATE = 'private'
export const VISIBILITY_SCHOOL = 'school'

const SCOPE_STORAGE_KEY = 'templateLibraryScope'

// Fields the server attaches to a template (or owns). A template opened with
// "Use template" is a fresh copy, so none of these may travel into the new
// post or into a template saved from it.
const TEMPLATE_ONLY_FIELDS = ['id', '_id', 'user_id', 'author', 'visibility', 'can_manage', 'created_date', 'updated_date', 'updated_by', 'targetSchools', '_enter']

// When and how often to post belongs to a post, not to a template: a template
// keeps only the content, so nothing old (e.g. a 2022 date) leaks into new posts.
const SCHEDULE_FIELDS = ['schedule_date', 'schedule_info', 'publicationDate', 'repeat', 'repeatData', 'repeatPeriodText']

export const isShared = (template) => template?.visibility === VISIBILITY_SCHOOL

/** Template payload without scheduling, for saving a template. */
export const withoutSchedule = (data = {}) => {
    const copy = { ...data, schedule: false }
    SCHEDULE_FIELDS.forEach((field) => delete copy[field])
    return copy
}

/** A detached, unscheduled copy of a template, safe to prefill a new post with. */
export const toTemplateCopy = (template = {}) => {
    const copy = withoutSchedule(template)
    TEMPLATE_ONLY_FIELDS.forEach((field) => delete copy[field])
    return copy
}

/** Display name of a template's author; "You" for the viewer's own. */
export const authorLabel = (author, user, t) => {
    if (!author) return ''
    if (user?.id && String(author.id) === String(user.id)) return t('templateAuthorYou')
    return `${author.first_name ?? ''} ${author.last_name ?? ''}`.trim()
}

const readScope = () => {
    try {
        const stored = window.localStorage.getItem(SCOPE_STORAGE_KEY)
        return TEMPLATE_SCOPES.includes(stored) ? stored : 'mine'
    } catch {
        return 'mine'
    }
}

/**
 * Which library tab is open. Remembered per browser as a convenience only;
 * storage can be unavailable (private mode), so every access is guarded.
 */
export const useTemplateScope = () => {
    const [scope, setScopeState] = useState(readScope)
    const setScope = useCallback((next) => {
        setScopeState(next)
        try {
            window.localStorage.setItem(SCOPE_STORAGE_KEY, next)
        } catch {
            // Remembering the tab is optional.
        }
    }, [])
    return [scope, setScope]
}
